import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { BuildHeader } from '../../Utils/headerBuilder'

/**
 * Redux state for managing template attachments on email templates
 * Handles CRUD operations for attaching templates to template emails
 * and automatic PDF generation when emails are sent
 */

// API Thunks
/**
 * Fetch all template attachments for a specific template email
 */
export const GetTemplateAttachments = createAsyncThunk(
  'GetTemplateAttachments',
  async (templateEmailId) => {
    if (!templateEmailId) {
      throw new Error('Template email ID is required')
    }

    const requestOptions = {
      method: 'GET',
      headers: BuildHeader(),
    }

    const response = await fetch(
      `${window.extended.MYAPP_API_ENDPOINT}/template/TemplateAttachments/${templateEmailId}`,
      requestOptions,
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch template attachments')
    }

    return await response.json()
  },
)

/**
 * Add a new template attachment to a template email
 */
export const AddTemplateAttachment = createAsyncThunk('AddTemplateAttachment', async (params) => {
  const { templateEmailId, templateId, sortOrder = 0 } = params

  if (!templateEmailId || !templateId) {
    throw new Error('Template email ID and template ID are required')
  }

  const requestOptions = {
    method: 'POST',
    headers: BuildHeader(),
    body: JSON.stringify({
      templateId: templateId,
      sortOrder: sortOrder,
    }),
  }

  const response = await fetch(
    `${window.extended.MYAPP_API_ENDPOINT}/template/TemplateAttachments/${templateEmailId}/add`,
    requestOptions,
  )

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to add template attachment')
  }

  return await response.json()
})

/**
 * Remove a template attachment from a template email
 */
export const RemoveTemplateAttachment = createAsyncThunk(
  'RemoveTemplateAttachment',
  async (params) => {
    const { templateEmailId, attachmentId } = params

    if (!templateEmailId || !attachmentId) {
      throw new Error('Template email ID and attachment ID are required')
    }

    const requestOptions = {
      method: 'DELETE',
      headers: BuildHeader(),
    }

    const response = await fetch(
      `${window.extended.MYAPP_API_ENDPOINT}/template/TemplateAttachments/${templateEmailId}/remove/${attachmentId}`,
      requestOptions,
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to remove template attachment')
    }

    return { attachmentId }
  },
)

/**
 * Update the sort order of a template attachment
 */
export const UpdateTemplateAttachmentSortOrder = createAsyncThunk(
  'UpdateTemplateAttachmentSortOrder',
  async (params) => {
    const { templateEmailId, attachmentId, newSortOrder } = params

    if (!templateEmailId || !attachmentId || newSortOrder === undefined) {
      throw new Error('Template email ID, attachment ID, and sort order are required')
    }

    const requestOptions = {
      method: 'PUT',
      headers: BuildHeader(),
      body: JSON.stringify(newSortOrder),
    }

    const response = await fetch(
      `${window.extended.MYAPP_API_ENDPOINT}/template/TemplateAttachments/${templateEmailId}/reorder/${attachmentId}`,
      requestOptions,
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update attachment sort order')
    }

    return await response.json()
  },
)

// Redux Slice
const initialState = {
  attachments: [],
  isLoading: false,
  isAddingAttachment: false,
  isRemovingAttachment: false,
  isReorderingAttachment: false,
  error: null,
  successMessage: null,
}

const TemplateAttachmentState = createSlice({
  name: 'TemplateAttachment',
  initialState,
  reducers: {
    // Clear error message
    ClearError: (state) => {
      state.error = null
    },
    // Clear success message
    ClearSuccess: (state) => {
      state.successMessage = null
    },
    // Reset all state
    ResetState: (state) => {
      return initialState
    },
  },
  extraReducers: {
    // Get template attachments
    [GetTemplateAttachments.pending]: (state) => {
      state.isLoading = true
      state.error = null
    },
    [GetTemplateAttachments.fulfilled]: (state, action) => {
      state.isLoading = false
      // Ensure attachments is always an array
      state.attachments = Array.isArray(action.payload) ? action.payload : []
    },
    [GetTemplateAttachments.rejected]: (state, action) => {
      state.isLoading = false
      state.error = action.error.message || 'Failed to load template attachments'
      state.attachments = []
    },

    // Add template attachment
    [AddTemplateAttachment.pending]: (state) => {
      state.isAddingAttachment = true
      state.error = null
    },
    [AddTemplateAttachment.fulfilled]: (state, action) => {
      state.isAddingAttachment = false
      state.attachments.push(action.payload)
    },
    [AddTemplateAttachment.rejected]: (state, action) => {
      state.isAddingAttachment = false
      state.error = action.error.message || 'Failed to add template attachment'
    },

    // Remove template attachment
    [RemoveTemplateAttachment.pending]: (state) => {
      state.isRemovingAttachment = true
      state.error = null
    },
    [RemoveTemplateAttachment.fulfilled]: (state, action) => {
      state.isRemovingAttachment = false
      state.attachments = state.attachments.filter((a) => a.id !== action.payload.attachmentId)
    },
    [RemoveTemplateAttachment.rejected]: (state, action) => {
      state.isRemovingAttachment = false
      state.error = action.error.message || 'Failed to remove template attachment'
    },

    // Update sort order
    [UpdateTemplateAttachmentSortOrder.pending]: (state) => {
      state.isReorderingAttachment = true
      state.error = null
    },
    [UpdateTemplateAttachmentSortOrder.fulfilled]: (state, action) => {
      state.isReorderingAttachment = false
      const index = state.attachments.findIndex((a) => a.id === action.payload.id)
      if (index !== -1) {
        state.attachments[index] = action.payload
      }
    },
    [UpdateTemplateAttachmentSortOrder.rejected]: (state, action) => {
      state.isReorderingAttachment = false
      state.error = action.error.message || 'Failed to update attachment order'
    },
  },
})

export const { ClearError, ClearSuccess, ResetState } = TemplateAttachmentState.actions
export default TemplateAttachmentState.reducer
